import { useState, type FormEvent } from "react";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import type { BrandVoice as BrandVoiceType } from "../../../../shared/types";

interface BrandVoiceProps {
  voice: BrandVoiceType | null;
  onSave: (data: Partial<BrandVoiceType>) => void;
  saving?: boolean;
}

export function BrandVoice({ voice, onSave, saving }: BrandVoiceProps) {
  const [tone, setTone] = useState(voice?.tone || "");
  const [guidelines, setGuidelines] = useState(voice?.guidelines || "");
  const [vocabInput, setVocabInput] = useState("");
  const [vocabulary, setVocabulary] = useState<string[]>(voice?.vocabulary || []);
  const [postInput, setPostInput] = useState("");
  const [examplePosts, setExamplePosts] = useState<string[]>(voice?.example_posts || []);

  function handleAddVocab() {
    if (vocabInput.trim() && !vocabulary.includes(vocabInput.trim())) {
      setVocabulary([...vocabulary, vocabInput.trim()]);
      setVocabInput("");
    }
  }

  function handleAddPost() {
    if (postInput.trim()) {
      setExamplePosts([...examplePosts, postInput.trim()]);
      setPostInput("");
    }
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    onSave({ tone, vocabulary, example_posts: examplePosts, guidelines });
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <Input
        label="Tone description"
        value={tone}
        onChange={(e) => setTone(e.target.value)}
        placeholder="e.g. Professional but approachable, uses clear language"
      />

      <div>
        <label className="mb-1.5 block text-sm font-medium text-slate-700">Vocabulary</label>
        <div className="mb-2 flex flex-wrap gap-2">
          {vocabulary.map((word) => (
            <span
              key={word}
              className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-sm text-slate-700"
            >
              {word}
              <button
                type="button"
                onClick={() => setVocabulary(vocabulary.filter((w) => w !== word))}
                className="ml-1 text-slate-400 hover:text-slate-600"
              >
                &times;
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <Input
            value={vocabInput}
            onChange={(e) => setVocabInput(e.target.value)}
            placeholder="Key term or jargon"
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); handleAddVocab(); }
            }}
          />
          <Button type="button" variant="secondary" onClick={handleAddVocab}>Add</Button>
        </div>
      </div>

      <div>
        <label className="mb-1.5 block text-sm font-medium text-slate-700">
          Example posts ({examplePosts.length})
        </label>
        {examplePosts.map((post, i) => (
          <div key={i} className="mb-2 flex items-start gap-2 rounded-lg border border-slate-200 p-3">
            <p className="flex-1 text-sm text-slate-600">{post}</p>
            <button
              type="button"
              onClick={() => setExamplePosts(examplePosts.filter((_, j) => j !== i))}
              className="text-slate-400 hover:text-red-500"
            >
              &times;
            </button>
          </div>
        ))}
        <textarea
          value={postInput}
          onChange={(e) => setPostInput(e.target.value)}
          placeholder="Paste an example post in your brand voice..."
          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          rows={3}
        />
        <Button type="button" variant="secondary" onClick={handleAddPost} className="mt-2">
          Add example
        </Button>
      </div>

      <div>
        <label className="mb-1.5 block text-sm font-medium text-slate-700">Voice guidelines</label>
        <textarea
          value={guidelines}
          onChange={(e) => setGuidelines(e.target.value)}
          placeholder="Any special writing guidelines, do's and don'ts..."
          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          rows={3}
        />
      </div>

      <Button type="submit" disabled={saving}>
        {saving ? "Saving..." : "Save voice settings"}
      </Button>
    </form>
  );
}
